import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './WindBlownView.css';

const WindBlownView = ({ poem, onBack }) => {
  const [settledWords, setSettledWords] = useState(new Set());
  const [allWordsSettled, setAllWordsSettled] = useState(false);
  const audioRef = useRef(null);

  // Split poem into words
  const lines = poem?.content?.split('\n') || [];
  const allWords = lines.flatMap((line, lineIndex) =>
    line.split(' ').map((word, wordIndex) => ({
      id: `${lineIndex}-${wordIndex}`,
      word,
      lineIndex,
      wordIndex,
      // Random starting position (for falling animation)
      startX: Math.random() * 90,
      startY: -10 - Math.random() * 20, // Start above screen
      // Diagonal drift for falling
      endX: Math.random() * 90,
      // Final position (where it should settle)
      finalX: 20 + (wordIndex % 8) * 10,
      finalY: 20 + lineIndex * 8,
      // Fall characteristics
      fallDuration: 20 + Math.random() * 10, // Slower than leaves
      fallDelay: Math.random() * 5
    }))
  );

  useEffect(() => {
    // Play breeze sound
    if (audioRef.current) {
      audioRef.current.volume = 0.2;
      audioRef.current.play().catch(() => {});
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  useEffect(() => {
    if (settledWords.size === allWords.length && allWords.length > 0) {
      setAllWordsSettled(true);
    }
  }, [settledWords.size, allWords.length]);

  const handleWordClick = (wordId, lineIndex, wordIndex) => {
    if (!settledWords.has(wordId)) {
      setSettledWords(prev => new Set([...prev, wordId]));

      // Auto-settle words in the same sentence after clicking one
      const sentenceWords = allWords.filter(w => w.lineIndex === lineIndex);
      setTimeout(() => {
        const newSettled = new Set(settledWords);
        sentenceWords.forEach(w => newSettled.add(w.id));
        setSettledWords(newSettled);
      }, 300);
    }
  };

  // Generate falling leaves for background - can fall from/to any x coordinate
  const leaves = Array.from({ length: 180 }, (_, i) => ({
    id: i,
    startX: Math.random() * 100, // Start from any x coordinate (0-100%)
    endX: Math.random() * 100,    // End at any x coordinate (0-100%)
    delay: Math.random() * 8,
    duration: 15 + Math.random() * 8,  // Slower, more graceful fall
    size: 20 + Math.random() * 15
  }));

  return (
    <motion.div
      className="windblown-view"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
    >
      {/* Fall colors gradient background */}
      <div className="windblown-background" />

      {/* Falling leaves */}
      <div className="leaves-container">
        {leaves.map(leaf => (
          <motion.div
            key={leaf.id}
            className="falling-leaf"
            style={{
              left: `${leaf.startX}%`,
              width: `${leaf.size}px`,
              height: `${leaf.size}px`
            }}
            initial={{ y: -50, x: 0, rotate: 0, opacity: 0 }}
            animate={{
              y: '110vh',
              x: `${leaf.endX - leaf.startX}vw`, // Diagonal movement to the right
              rotate: [0, 180, 360, 540],
              rotateX: [0, 180, 360],  // Flip animation
              rotateY: [0, 180, 0],    // Additional flip
              opacity: [0, 0.7, 0.7, 0.6, 0]
            }}
            transition={{
              delay: leaf.delay,  // Stagger the appearance of leaves
              duration: leaf.duration,
              repeat: Infinity,
              ease: "easeInOut",  // Smoother, more graceful easing
              rotate: {
                duration: leaf.duration,
                ease: "linear"
              },
              rotateX: {
                duration: leaf.duration * 0.7,
                ease: "easeInOut",
                repeat: Infinity
              },
              rotateY: {
                duration: leaf.duration * 0.5,
                ease: "easeInOut",
                repeat: Infinity
              }
            }}
          >
            {/* Leaf SVG */}
            <svg viewBox="0 0 40 40" className="leaf-svg">
              <path
                d="M 20 5 Q 30 15, 30 25 Q 30 35, 20 35 Q 10 35, 10 25 Q 10 15, 20 5"
                fill={`hsl(${20 + Math.random() * 40}, 70%, 50%)`}
                opacity="0.8"
              />
              <line x1="20" y1="5" x2="20" y2="35" stroke="#8B4513" strokeWidth="1" opacity="0.6"/>
            </svg>
          </motion.div>
        ))}
      </div>

      {/* Title */}
      <motion.h2
        className="windblown-title"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 1 }}
      >
        {poem?.title || 'Untitled'}
      </motion.h2>

      {/* Floating/Settled words */}
      <div className="windblown-content">
        <AnimatePresence>
          {allWords.map((wordObj) => {
            const isSettled = settledWords.has(wordObj.id);

            return (
              <motion.div
                key={wordObj.id}
                className={`windblown-word ${isSettled ? 'settled' : 'floating'}`}
                onClick={() => handleWordClick(wordObj.id, wordObj.lineIndex, wordObj.wordIndex)}
                initial={{
                  x: `${wordObj.startX}vw`,
                  y: `${wordObj.startY}vh`,
                  opacity: 0,
                  scale: 0.5
                }}
                animate={isSettled ? {
                  x: `${wordObj.finalX}vw`,
                  y: `${wordObj.finalY}vh`,
                  opacity: 1,
                  scale: 1,
                  rotate: 0
                } : {
                  // Falling animation like leaves: top to bottom, then loop
                  y: [`${wordObj.startY}vh`, '110vh'],
                  x: [`${wordObj.startX}vw`, `${wordObj.endX}vw`],
                  opacity: [0, 0.85, 0.85, 0.85, 0.6],
                  rotate: [0, 180, 360, 540],
                  scale: [0.5, 1, 1, 1, 0.8]
                }}
                transition={isSettled ? {
                  duration: 3,  // Much slower, more graceful settling
                  type: "spring",
                  stiffness: 50,  // Even less stiff for smoother motion
                  damping: 25     // More damping for graceful deceleration
                } : {
                  duration: wordObj.fallDuration,  // Slower than leaves
                  repeat: Infinity,
                  ease: "linear",
                  delay: wordObj.fallDelay,
                  opacity: { duration: 2 }  // Slower fade-in
                }}
                whileHover={!isSettled ? {
                  scale: 1.2,
                  textShadow: "0 0 8px rgba(217, 119, 6, 0.8)"
                } : {}}
                style={{
                  cursor: isSettled ? 'default' : 'pointer',
                  position: 'absolute',
                  lineHeight: wordObj.lineIndex === 0 ? '1.8' : '1.6'
                }}
              >
                {wordObj.word}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Signature box - centered at bottom */}
      <motion.div
        className="windblown-signature"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 1 }}
      >
        — {poem?.author || 'Anonymous'}
      </motion.div>

      {/* Breeze audio */}
      <audio ref={audioRef} loop preload="auto">
        <source src="/sounds/breeze.mp3" type="audio/mpeg" />
        <source src="/sounds/breeze.ogg" type="audio/ogg" />
      </audio>

      {/* Back button */}
      <motion.button
        className="windblown-back-button"
        onClick={onBack}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        whileHover={{
          backgroundColor: "rgba(217, 119, 6, 0.3)"
        }}
        whileTap={{ scale: 0.95 }}
      >
        ← Return to Garden
      </motion.button>
    </motion.div>
  );
};

export default WindBlownView;
